import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgxImageCompressService } from 'ngx-image-compress';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PotreeCesiumViewerService } from 'src/app/components/potree-cesium-viewer/potree-cesium-viewer.service';
import { ITaskForm } from 'src/app/components/task-form/task-form.component';
import { dataURLtoFile } from 'src/app/helpers/file.helper';
import { LS } from 'src/app/helpers/localStorage.enum';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { ChatService } from 'src/app/services/chat/chat.service';

import { DataService } from 'src/app/services/data/data.service';
import { FileService } from 'src/app/services/file/file.service';
import { LocationComponentService } from 'src/app/services/locationComponent/location-component.service';
import { MapService } from 'src/app/services/map/map.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { GenericTypeService } from 'src/app/services/generic-type/generic-type.service';
import { TaskService } from 'src/app/services/task/task.service';

import noUiSlider from 'nouislider';
import { LocationUIService } from 'src/app/services/location-ui/location-ui.service';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { SplitViewService } from 'src/app/services/split-view/split-view.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { GenericTypeStoreService } from 'src/app/stores/generic-types-store/generic-type-store.service';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { marker as _t_marker } from '@biesbjerg/ngx-translate-extract-marker';
import { OpenlayerService } from 'src/app/services/openlayer/openlayer.service';

declare var $;
declare let html2canvas: any;

@Component({
  selector: 'app-create-annotation',
  templateUrl: './create-annotation.component.html',
  styleUrls: ['./create-annotation.component.sass'],
})
export class CreateAnnotationComponent implements OnInit, OnDestroy {
  form: FormGroup;
  chatForm: FormGroup;
  taskForm: {
    /** For populating form */
    _value: ITaskForm;
    /** For submitting form (value retrieved from app-task-form) */
    value: ITaskForm;
    valid: boolean;
  };
  galleryForm: FormGroup;

  severityList = [];
  locationComponentList = [];

  currentLocation;
  actionBtn: boolean;
  isFormShow: Array<any> = [];
  mapLayerList = [];
  chats: Array<any> = [];
  currentUser: any;
  defectTypes = [];
  isOrtho: boolean;
  isDSM: boolean;
  isThermal: boolean;
  isThreeD: boolean;
  isThreeDMesh: boolean;
  isEmbed: boolean;
  isGeojson: boolean;
  isFacade: boolean;
  mapScreenshot: any;
  mapLayerType;
  createTitle = _t_marker(
    'Creating annotation on {{layer_type}} - {{layer_date}}'
  );

  /** currently selected ortho in splitscreen */
  currOrtho: any;

  isSplitScreenOn = false;

  /** Stores the route the user will go to after create-annotation */
  exitRoute: string;

  unsubber: Subject<boolean> = new Subject<boolean>();

  isSquareMeter = true;
  showAreaSize = true;

  constructor(
    private formBuilder: FormBuilder,
    private mapService: MapService,
    private dataService: DataService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private annotationService: AnnotationService,
    private genericTypeService: GenericTypeService,
    private chatService: ChatService,
    private taskService: TaskService,
    private locationComponentService: LocationComponentService,
    private potreeCesiumViewerService: PotreeCesiumViewerService,
    private toastr: ToastrService,
    private fileService: FileService,
    private spinner: NgxSpinnerService,
    private navigationService: NavigationService,
    private layerStore: LayerStoreService,
    private _t: TranslateService,
    private imageCompress: NgxImageCompressService,
    private locationUiService: LocationUIService,
    private splitViewService: SplitViewService,
    private locationStore: LocationStoreService,
    private genericTypeStore: GenericTypeStoreService,
    private datePipe: DatePipe,
    private titlecasePipe: TitleCasePipe,
    private openlayerService: OpenlayerService
  ) {}

  ngOnInit(): void {
    this.mapService.isShiftKeyDown$.next(false);
    this.mapService.removeAllGrid();
    this.mapService.allDrawings = [];
    this.mapService.allGridsData = [];

    //this.mapScreenshot.annotationAreaSizeWithGrid$.next(0);
    // this.potreeCesiumViewerService.removeAllMeasurements()
    this.initForms();

    this.defectTypes = this.genericTypeStore.defectTypeList;
    this.locationComponentList = this.dataService.locationComponentList;

    this.currentUser = JSON.parse(localStorage.getItem(LS.currentUser));
    this.locationStore.currLocation$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((currLoc) => {
        if (currLoc.id) {
          /** As per #141, all annotation will be turned off during drawing a new annotation */
          // this.mapService.toggleAllAnnotation(false, undefined, undefined);
          this.layerStore.toggleAllAnnotationDataLayer(false);

          this.genericTypeService
            .findAll()
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              this.genericTypeStore.setGenericTypes = res.data;
              this.defectTypes = this.genericTypeStore.defectTypeList;

              if (currLoc?.generic_type_template?.severity_set) {
                this.severityList =
                  currLoc.generic_type_template.severity_set.map((i) => {
                    return this.genericTypeStore.severityList.find(
                      (s) => s.id === i
                    );
                  });
              } else {
                this.severityList = this.genericTypeStore.severityList;
              }

              if (currLoc?.generic_type_template?.annotation_set) {
                this.defectTypes =
                  currLoc.generic_type_template.annotation_set.map((i) => {
                    return this.genericTypeStore.defectTypeList.find(
                      (s) => s.id === i
                    );
                  });
              } else {
                this.defectTypes = this.genericTypeStore.defectTypeList;
              }
            });

          this.currentLocation = currLoc;
          this.getLocationComponentList();
          const ortho = currLoc?.map_layer_ortho;
          const dsm = currLoc?.map_layer_dsm;
          const threeD = currLoc?.map_layer_3d;
          const thermal = currLoc?.map_layer_thermal;
          const threeDMesh = currLoc?.map_layer_3d_mesh;
          const facade = currLoc?.map_layer_facade;
          if (ortho) {
            this.mapLayerList.push(ortho);
            this.mapService.isOrtho$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((res) => {
                this.isOrtho = res;
                this.reselectMapLayer();
              });
          }
          if (dsm) {
            this.mapLayerList.push(dsm);
            this.mapService.isDSM$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((res) => {
                this.isDSM = res;
                this.reselectMapLayer();
              });
          }
          if (thermal) {
            this.mapLayerList.push(thermal);
            this.mapService.isThermal$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((res) => {
                this.isThermal = res;
                this.reselectMapLayer();
              });
          }
          if (threeDMesh) {
            this.mapLayerList.push(threeDMesh);
            this.mapService.isThreeDMesh$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((res) => {
                this.isThreeDMesh = res;
                this.reselectMapLayer();
              });
          }
          if (threeD) {
            this.mapLayerList.push(threeD);
            this.mapService.isThreeD$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((res) => {
                this.isThreeD = res;
                this.reselectMapLayer();
              });
          }
          if (facade) {
            this.mapLayerList.push(facade);
            this.layerStore.isFacade$.subscribe((stat) => {
              if (stat?.id) {
                this.isFacade = true;
                this.reselectMapLayer();
              } else {
                this.isFacade = false;
              }
            });
          }
        }
      });

    this.router.events
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.exitRoute = event.url;
        }
      });

    this.mapService.activeAnnotation$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((activeAnnot) => {
        // check if there are any drawings on map
        // if no drawing, reset all
        if (!activeAnnot?.drawing?.length) {
          this.form.patchValue({
            area: null,
            area_size: '0.000',
            area_centroid: null,
          });
          this.mapScreenshot = null;
          return;
        }

        // Multi grid area selection (using Shift) issue
        // https://app.clickup.com/t/hpp1e3
        // this.mapService.isShiftKeyDown$.next(false);

        // if there is a valid drawings
        this.form.patchValue({
          area: JSON.stringify(activeAnnot),
          area_size: this.toThreeDecimal(
            this.mapService.annotationAreaSizeWithGrid$.getValue()
          ),
          area_centroid: this.mapService
            .calculateCentroid(activeAnnot.drawing[0].geometry)
            .join(', '),
        });

        this.mapService._setImagesRelatedToCoor(
          activeAnnot.drawing[0].geometry
        );

        setTimeout(() => {
          const availableFeatureIds = this.mapService.draw
            .getAll()
            .features.map((i) => i.id);
          const activeAnnotFeatureIds = activeAnnot.drawing.map((i) => i.id);

          this.mapService.changeDrawMode('simple_select', {
            featureIds: activeAnnotFeatureIds.filter((ids) =>
              availableFeatureIds.includes(ids)
            ),
          });
          if (!this.mapService.isGrid$.getValue()) {
            this.mapService.toggleGrid(true);
          }
        });
        this.setMapScreenshot();
      });

    this.mapService.active3dAnnotation$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((active3dAnnot) => {
        if (active3dAnnot.length > 0) {
          this.form.patchValue({
            area: JSON.stringify(active3dAnnot),
          });
        }
      });

    this.openlayerService.currentAnnot$.subscribe((annot) => {
      // console.log(annot)
      this.mapScreenshot = annot.openlayerScreenshot;
      this.form.patchValue({
        area: annot.openlayerDrawings,
        area_size: '0.000',
        area_thumbnail: annot.openlayerScreenshot,
        map_layer: annot.maplayerid,
      });
    });

    this.potreeCesiumViewerService.activeMeasurement$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((measurement) => {
        if (this.isThreeD && measurement?.name) {
          if (measurement?.name) {
            const m = measurement;
            JSON.stringify(m);
          }
          // this.form.patchValue({
          //   area: JSON.stringify(measurement),
          //   // area_size: area.drawing ? this.mapService.calculateArea(area.drawing[0].geometry) : null
          // })
        }
      });

    this.mapService.annotationAreaSizeWithGrid$.next(0);
    this.mapService.annotationAreaSizeWithGrid$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((size) => {
        // Multi grid area selection (using Shift) issue
        // https://app.clickup.com/t/hpp1e3
        // this.mapService.isShiftKeyDown$.next(false);
        this.isSquareMeter =
          this.mapService.allDrawings[0]?.geometry.type === 'LineString'
            ? false
            : true;
        this.form.patchValue({
          area: JSON.stringify(this.mapService.activeAnnotation$.getValue()),
          area_size: this.toThreeDecimal(size),
        });
        if (size > 0) this.setMapScreenshot();
      });

    // WIP : get centroid based on multiple drawings
    // this.mapService.annotationCentroidWithGrid$
    // .pipe(takeUntil(this.unsubber.asObservable()))
    // .subscribe(centroid => {

    //   this.form.patchValue({
    //     area_centroid: centroid
    //   });
    // });

    this.mapService.threeDScreenshotBlob$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((blob) => {
        this.imageCompress.compressFile(blob, -1, 50, 50).then((result) => {
          this.mapScreenshot = result;
        });
      });

    this.dataService.sticky$.next(['toolkit']);
    this.dataService.featureImages$.next([]);

    this.splitViewService.onEvent$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        if (res.onClickRemove) {
          this.onCancelClicked();
        }
      });

    if (this.mapService.isMapLoaded$ && this.splitViewService.mapViews) {
      this.splitViewService.isEditingAnnotation = true;
      this.isSplitScreenOn = this.splitViewService.isSplitscreenOn;
      const highlightedOrtho =
        this.splitViewService.mapViews[
          this.splitViewService.selectedMapViewIndex
        ].ortho;
      if (highlightedOrtho) {
        this.currOrtho = highlightedOrtho;
        // const titleTranslate = this._t.instant('Creating annotation on {{layer_type}} - {{layer_date}}', {
        //   "layer_type": this.titlecasePipe.transform(this._t.instant(highlightedOrtho['layer_type'])),
        //   "layer_date": this.datePipe.transform(highlightedOrtho['layer_date'], 'dd/MM/yyyy')
        // })
        // this.createTitle = 'Creating annotation on {{layer_type}} - {{layer_date}}'

        this.form.patchValue({
          map_layer: highlightedOrtho.id,
        });
      }
    }

    this.layerStore.isFacade$.subscribe((res) => {
      this.showAreaSize = !res;
    });
    this.openlayerService.savedAnnot$.next({
      openlayerDrawings: undefined,
      enableEdit: new Date().toISOString(),
    });
  }

  toThreeDecimal(area_size: number | string) {
    return Number(area_size).toFixed(3);
  }

  ngAfterViewInit(): void {
    // instantiate grid rotate slider with disabled attr
    setTimeout(() => {
      if ($('.slider-grid')[0]) {
        if ($('.slider-grid')[0]['noUiSlider']) {
          $('.slider-grid')[0]['noUiSlider'].destroy();
        }
        const rotateSlider = noUiSlider.create($('.slider-grid')[0], {
          start: [0],
          range: {
            min: [-180],
            max: [180],
          },
          step: 2,
        });
      }
      $('.slider-grid').attr('disabled', true);
      $('.rotateInput').attr('disabled', true);
      $('.reset-grid-button').attr('disabled', true);
      // https://gitlab.com/aerodynegroup/suntory/-/merge_requests/281/diffs
      // multiple annotation is disabled, so this fix is not used anymore
      // this.mapService.removeAllGrid();
      // this.mapService.allDrawings = [];
      // this.mapService.allGridsData = [];
    }, 2000);
  }

  toggleFormIsShow(formName) {
    if (this.isFormShow.includes(formName)) {
      const index = this.isFormShow.indexOf(formName);
      if (index > -1) {
        if (formName === 'task') {
          this.taskForm = {} as any;
        }
        this.isFormShow.splice(index, 1);
      }
    } else {
      this.isFormShow.push(formName);
    }
  }

  initForms(): void {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      location: ['', Validators.required],
      location_component: ['', Validators.required],
      annotation_type: ['', Validators.required],
      severity_type: ['', Validators.required],
      map_layer: ['', Validators.required],
      area: [{ value: '', disabled: true }, Validators.required],
      area_size: [{ value: 0, disabled: true }, Validators.required],
      area_thumbnail: '',
      area_centroid: '',
      kp: '',
      flight_date: '',
      status: '',
    });
    this.chatForm = this.formBuilder.group({
      sender_id: '',
      message: '',
      defect_id: '',
    });
    this.taskForm = {} as any;
    this.galleryForm = this.formBuilder.group({
      file: '',
      defect_id: '',
    });
  }

  onSubmit() {
    this.mapService.lastActiveDrawId = '';
    this.spinner.show();

    this.form.patchValue({
      location: this.locationStore.currentLocation.id,
    });
    if (this.taskForm.value) {
      if (!this.taskForm.valid) {
        this.spinner.hide();
        this.toastr.warning(
          this._t.instant('Task form not valid'),
          this._t.instant('Error')
        );
      }
    }

    if (this.form.invalid) {
      this.spinner.hide();

      const errors = [];
      Object.keys(this.form.controls).map((control) => {
        const errs = this.form.get(control).errors;

        if (!errs) return;

        let controlText = '';
        switch (control) {
          case 'name': {
            controlText = this._t.instant('Name');
            break;
          }
          case 'description': {
            controlText = this._t.instant('Description');
            break;
          }
          case 'location': {
            controlText = this._t.instant('Location');
            break;
          }
          case 'location_component': {
            controlText = this._t.instant('Location Component');
            break;
          }
          case 'annotation_type': {
            controlText = this._t.instant('Defect Type');
            break;
          }
          case 'area': {
            controlText = this._t.instant('Area');
            break;
          }
          case 'area_size': {
            controlText = this._t.instant('Area size');
            break;
          }
          case 'severity_type': {
            controlText = this._t.instant('Severity');
            break;
          }
          case 'map_layer': {
            controlText = this._t.instant('Map Layer');
            break;
          }
        }

        Object.keys(errs).map((err) => {
          let text;
          switch (err) {
            case 'required': {
              text = this._t.instant('{{field}} is required', {
                field: controlText,
              });
              break;
            }
          }

          errors.push(text);
        });
      });
      this.toastr.error(errors.join('</br>'), this._t.instant('Error'), {
        enableHtml: true,
        timeOut: 5000,
      });
    } else {
      this._createNewAnnot();
    }
    // this.mapService.updateActiveAnnotation();
  }

  _createNewAnnot(callback?) {
    const form = this.form.getRawValue();
    if (this.splitViewService.isSplitscreenOn) {
      form.map_layer = this.splitViewService.selectedOrtho.id;
    } //console.log(form);return;
    if (form.map_layer.length < 2) delete form.map_layer;
    this.spinner.show();

    //return; // for debug
    this.annotationService
      .createAnnotation(form)
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe({
        next: async (createdAnnot) => {
          if (createdAnnot) {
            const area = JSON.parse(form.area);
            area.id = createdAnnot.id;
            this.openlayerService.appendOneAllSavedAnnot = JSON.stringify(area);
            if (this.chats.length > 0) {
              this.chatService
                .createThread({ annotation: createdAnnot.id, content: '' })
                .subscribe((thread) => {
                  this.chats.forEach((i, k) => {
                    this.chatService
                      .create({
                        chat_thread: thread.id,
                        content: i.content,
                      })
                      .subscribe();
                  });
                });
            }
            if (this.taskForm.value) {
              this.taskService
                .create({ ...this.taskForm.value, annotation: createdAnnot.id })
                .subscribe(
                  (task) => {},
                  ({ error }) => {
                    const msg = error.message
                      .reduce(
                        (results, u) => ((results += u + ', '), results),
                        ''
                      )
                      .slice(0, -2);
                    this.toastr.error(
                      this._t.instant(msg),
                      this._t.instant('Error')
                    );
                  }
                );
            }

            if (this.mapScreenshot) {
              const file = dataURLtoFile(
                this.mapScreenshot,
                `area_thumbnail_${
                  this.locationStore.currentLocation.id
                }_${Date.now()}`
              );

              //upload area_thumnbnail to s3
              const formData = new FormData();
              formData.append('annotation', file);
              formData.append(
                'location',
                this.locationStore.currentLocation.id
              );
              formData.append('annotation_id', createdAnnot.id);

              this.fileService
                .create(formData)
                .pipe(takeUntil(this.unsubber.asObservable()))
                .subscribe(async (createdFile) => {
                  this.mapService.threeDScreenshotBlob$.next(null);
                  await this.annotationService
                    .update(createdAnnot.id, {
                      area_thumbnail: createdFile[0].url,
                      annotation_file: createdFile[0].id,
                    })
                    .toPromise();
                  await this.locationUiService.refreshDefectCounts(
                    this.locationStore.currentLocation.id
                  );
                  this.toastr.success(
                    this._t.instant('Successfully annotate'),
                    this._t.instant('Success')
                  );
                  this.spinner.hide();
                  this.navigationService.back();
                });
            } else {
              await this.locationUiService.refreshDefectCounts(
                this.locationStore.currentLocation.id
              );
              this.toastr.success(
                this._t.instant('Successfully annotate'),
                this._t.instant('Success')
              );
              this.spinner.hide();
              this.navigationService.back();
            }
          }

          if (callback) {
            callback(createdAnnot);
          }
        },
        error: ({ error }) => {
          this.spinner.hide();
          const msg = error.message
            .reduce((results, u) => ((results += u + ', '), results), '')
            .slice(0, -2);
          this.toastr.error(msg, this._t.instant('Error'));
        },
      });
  }

  // onFileSelected(e) {
  //   let fileName = e.target.value.split('\\').pop();
  //   e.target.nextElementSibling.classList.add('selected');
  //   e.target.nextElementSibling.innerHTML = fileName;
  // }

  getLocationComponentList(): void {
    this.locationComponentService
      .findAll([
        { key: 'filtered[0][key]', val: 'location' },
        { key: 'filtered[0][val]', val: this.currentLocation.id },
      ])
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        this.locationComponentList = res.data;
        this.dataService.locationComponentList = res.data;
      });
  }

  ngOnDestroy() {
    this.openlayerService.savedAnnot$.next({
      openlayerScreenshot: null,
      enableEdit: undefined,
    });
    this.splitViewService.isEditingAnnotation = false;
    this.mapService.removeAllGrid();
    this.mapService.removeAllLineStringMarkers();
    this.mapService.activeAnnotation$.next(null);
    this.mapService.isDraw$.next(null);
    this.mapService.hasDrawing$.next(false);
    // this.mapService.threeDScreenshotBlob$.next(null)
    const allDraw = this.mapService.draw.getAll();
    if (allDraw.features.length > 0) {
      const drawIds = allDraw.features.reduce(
        (acc, item) => [...acc, item.id],
        []
      );
      this.mapService.draw.delete(drawIds);
    }

    // if (this.mapService.activeAnnotation$.getValue()) {
    //   this.mapService.deleteSelectedAnnot(
    //     this.mapService.activeAnnotation$.getValue().id
    //   );
    // }

    this.layerStore.resetMapLayer();

    if (this.exitRoute.includes('details')) {
      this.mapService.threeDScreenshotBlob$.next(null);
      this.locationUiService.refreshCurrLocation(this.currentLocation.id);
    }

    this.dataService.sticky$.next([]);
    this.dataService.featureImages$.next([]);
    this.layerStore.toggleAllAnnotationDataLayer(true);

    this.unsubber.next();
    this.unsubber.unsubscribe();
  }

  toggleActionBtn() {
    this.actionBtn = !this.actionBtn;
  }

  sendChat() {
    const message = this.chatForm.get('message').value.trim();
    const chat = {
      content: message,
      sender: {
        id: this.currentUser.user_id,
        username: this.currentUser.username,
      },
    };
    if (message.length > 1) {
      this.chats.push(chat);
      this.chatForm.controls['message'].reset();
    }
  }

  reselectMapLayer() {
    let map_layer_id = '';
    if (this.isThreeD) {
      map_layer_id = this.currentLocation.map_layer_3d?.id;
    } else if (this.isOrtho) {
      map_layer_id = this.currentLocation.map_layer_ortho?.id;
    } else if (this.isFacade) {
      map_layer_id = this.currentLocation.map_layer_facade?.id;
    } //console.log(map_layer_id)
    this.form.patchValue({ map_layer: map_layer_id });
  }

  showRespectiveMap(mapId) {
    const map_layer = this.mapLayerList.find((i) => i.id === mapId);
    this.layerStore.toggleMapLayer(
      map_layer.layer_type,
      this.locationStore.currentLocation.id,
      map_layer,
      true
    );
  }

  changeSeverity(severity_id) {
    const allDraw = this.mapService.draw.getAll();

    let severity = this.severityList.filter(
      (severity) => severity.id === severity_id
    );
    if (severity.length > 0) {
      severity = severity[0];
    }
    if (this.layerStore.isFacade$.getValue()) {
      this.openlayerService.drawingColor$.next(severity['description']);
    }

    if (allDraw.features.length > 0) {
      allDraw.features.forEach((i) => {
        this.mapService.draw.setFeatureProperty(
          i.id,
          'severityColor',
          severity_id
        );
        this.mapService.draw.add(this.mapService.draw.get(i.id));
        this.setMapScreenshot();
      });
    }
  }

  mapScreenshotDelay;

  setMapScreenshot() {
    this.mapService.isDraw$.next(null);
    if (this.isThreeD) {
      this.setMapScreenshot3D();
      return;
    }
    if (this.mapScreenshotDelay) {
      clearTimeout(this.mapScreenshotDelay);
    }
    // make screenshit appear first so that the system feels faster
    setTimeout(() => {
      this.mapScreenshot = this.mapService.getMapScreenshot();
    }, 300);
    this.mapScreenshotDelay = setTimeout(() => {
      // then update the screenshit with compressed version
      this.imageCompress
        .compressFile(this.mapService.getMapScreenshot(), -1, 50, 50)
        .then((result) => {
          this.mapScreenshot = result;
        });
    }, 2000);
  }

  setMapScreenshot3D() {
    html2canvas(document.querySelector('#potree_render_area')).then(
      (canvas) => {
        const blob = canvas.toDataURL('image/png');
        this.imageCompress.compressFile(blob, -1, 50, 50).then((result) => {
          this.mapScreenshot = result;
        });
      }
    );
  }

  onCancelClicked(clickEvt?: any) {
    this.form.reset();
    this.router.navigate(['../'], { relativeTo: this.activatedRoute });
  }
}
